import { useCallback, useEffect, useRef, useState } from 'react';
import { notifications } from '@mantine/notifications';
import { approvalsService } from '../../../../../services/approvals/approvals.service';
import type { ApprovalActionType, ApprovalPayload, ApprovalReason, ApprovalRequestView, ApprovalStatus } from '../../../../../services/approvals/approvals.service';
import { toHebrewApiErrorMessage } from '../../../../../services/index/index.service';

export type { ApprovalActionType, ApprovalPayload, ApprovalReason, ApprovalRequestView };

type UseApprovalsParams = {
  tenantId: string;
  employeeSession: string | null;
  activeSessionId: number | null;
  // null = orders-list mode (loads all pending across orders)
  // number = open-order mode (loads pending for this order only, employee request flow)
  orderId: number | null;
  isManager: boolean;
  // Actor-side self-heal after create success (handles rare missed/burst SSE frames).
  onApprovalSubmitted?: () => Promise<void> | void;
};

type UseApprovalsReturn = {
  // Tenant-wide pending count — used for the filter card badge in orders-list mode
  allPendingCount: number;
  // Requests shown in the manager modal — managers always need the tenant-wide queue.
  pendingRequests: ApprovalRequestView[];
  historyRequests: ApprovalRequestView[];
  approvalHistoryFilter: 'all' | Extract<ApprovalStatus, 'approved' | 'denied'>;
  setApprovalHistoryFilter: (filter: 'all' | Extract<ApprovalStatus, 'approved' | 'denied'>) => void;
  isLoadingHistory: boolean;
  hasMoreHistory: boolean;
  loadMoreHistory: () => void;
  // Pending requests for the selected order, used for in-order indicators/blocking.
  orderPendingRequests: ApprovalRequestView[];
  // True when the selected order has a pending request (blocks payment screen)
  hasPendingRequest: boolean;
  approvalsModalOpened: boolean;
  openApprovalsModal: () => void;
  closeApprovalsModal: () => void;
  approvalRequestModalState: {
    opened: boolean;
    actionType: ApprovalActionType;
    payload: ApprovalPayload;
  } | null;
  isSubmittingApproval: boolean;
  isDeciding: boolean;
  requestApproval: (actionType: ApprovalActionType, payload: ApprovalPayload) => void;
  closeApprovalRequestModal: () => void;
  submitApproval: (reason: ApprovalReason, note: string | null) => Promise<void>;
  onApprovalEvent: (request: ApprovalRequestView) => void;
  decide: (requestId: number, decision: 'approved' | 'denied') => Promise<void>;
  // Logs a manager self-action as an auto-approved record.
  logManagerAction: (payload: ApprovalPayload) => Promise<void>;
};

export function useApprovals({ tenantId, employeeSession, activeSessionId, orderId, isManager, onApprovalSubmitted }: UseApprovalsParams): UseApprovalsReturn {
  // All pending across the tenant — loaded when manager, kept in sync via SSE
  const [allPending, setAllPending] = useState<ApprovalRequestView[]>([]);
  const [sessionHistory, setSessionHistory] = useState<ApprovalRequestView[]>([]);
  const [historyCursor, setHistoryCursor] = useState<number | null>(null);
  const [hasMoreHistory, setHasMoreHistory] = useState(false);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [approvalHistoryFilter, setApprovalHistoryFilter] = useState<'all' | 'approved' | 'denied'>('all');
  // Per-order pending — subset of allPending filtered by orderId
  const [approvalsModalOpened, setApprovalsModalOpened] = useState(false);
  const [approvalRequestModalState, setApprovalRequestModalState] = useState<{
    opened: boolean;
    actionType: ApprovalActionType;
    payload: ApprovalPayload;
  } | null>(null);
  const [isSubmittingApproval, setIsSubmittingApproval] = useState(false);
  const [isDeciding, setIsDeciding] = useState(false);
  const loadedRef = useRef(false);
  const submitInFlightRef = useRef(false);
  const decideInFlightRef = useRef(false);
  const historyLoadInFlightRef = useRef(false);
  const historyInitialLoadedRef = useRef(false);

  // Load all pending once per session (manager only)
  useEffect(() => {
    if (!isManager || !employeeSession || loadedRef.current) return;
    loadedRef.current = true;
    approvalsService.listAllPending(tenantId, employeeSession).then((list) => {
      setAllPending(list);
    }).catch(() => {
      // silently ignore
    });
  }, [tenantId, employeeSession, isManager]);

  // Reset loader when session changes
  useEffect(() => {
    loadedRef.current = false;
    setAllPending([]);
    setSessionHistory([]);
    setHistoryCursor(null);
    setHasMoreHistory(false);
    setApprovalHistoryFilter('all');
    historyInitialLoadedRef.current = false;
  }, [tenantId, employeeSession, activeSessionId]);

  const loadHistoryPage = useCallback(async (cursor: number | null) => {
    if (!isManager || !employeeSession || historyLoadInFlightRef.current) {
      return;
    }
    historyLoadInFlightRef.current = true;
    setIsLoadingHistory(true);
    try {
      const page = await approvalsService.listCurrentSessionHistory(tenantId, employeeSession, cursor);
      setSessionHistory((previous) => cursor === null
        ? page.rows
        : [...previous, ...page.rows.filter((request) => !previous.some((existing) => existing.id === request.id))]);
      setHistoryCursor(page.nextCursor);
      setHasMoreHistory(page.nextCursor !== null);
      if (cursor === null) {
        historyInitialLoadedRef.current = true;
      }
    } catch {
      // The pending queue remains usable if history cannot be loaded.
    } finally {
      setIsLoadingHistory(false);
      historyLoadInFlightRef.current = false;
    }
  }, [employeeSession, isManager, tenantId]);

  useEffect(() => {
    if (!approvalsModalOpened || allPending.length > 0 || historyInitialLoadedRef.current || isLoadingHistory) {
      return;
    }
    void loadHistoryPage(null);
  }, [approvalsModalOpened, allPending.length, isLoadingHistory, loadHistoryPage]);

  // Load pending state for the active order as well. Employees do not load the
  // tenant-wide manager list, but still need to know an order is blocked.
  useEffect(() => {
    if (!employeeSession || orderId === null) return;

    let cancelled = false;
    approvalsService.listPending(tenantId, orderId, employeeSession).then((list) => {
      if (cancelled) return;
      setAllPending((previous) => [
        ...previous.filter((request) => request.orderId !== orderId),
        ...list,
      ]);
    }).catch(() => {
      // The action submit path still returns the authoritative error.
    });

    return () => {
      cancelled = true;
    };
  }, [tenantId, employeeSession, orderId]);

  const onApprovalEvent = useCallback((request: ApprovalRequestView) => {
    setAllPending((previous) => {
      const without = previous.filter((r) => r.id !== request.id);
      if (request.status === 'pending') return [...without, request];
      return without;
    });
    if (request.status !== 'pending') {
      setSessionHistory((previous) => [
        request,
        ...previous.filter((existing) => existing.id !== request.id),
      ]);
    }
  }, []);

  const requestApproval = useCallback((actionType: ApprovalActionType, payload: ApprovalPayload) => {
    setApprovalRequestModalState({ opened: true, actionType, payload });
  }, []);

  const closeApprovalRequestModal = useCallback(() => {
    setApprovalRequestModalState(null);
  }, []);

  const submitApproval = useCallback(async (reason: ApprovalReason, note: string | null) => {
    if (!approvalRequestModalState || !orderId || !employeeSession || submitInFlightRef.current) return;
    submitInFlightRef.current = true;
    setIsSubmittingApproval(true);
    try {
      const created = await approvalsService.create(tenantId, orderId, {
        actionType: approvalRequestModalState.actionType,
        reason,
        note,
        payload: approvalRequestModalState.payload,
      }, employeeSession);
      onApprovalEvent(created);
      if (onApprovalSubmitted) {
        try {
          await onApprovalSubmitted();
        } catch {
          // non-critical — the approval write already succeeded; SSE/deferred refresh still converges.
        }
      }
      setApprovalRequestModalState(null);
      if (!isManager) {
        notifications.show({
          color: 'blue',
          title: 'הבקשה נשלחה',
          message: 'ממתין לאישור מנהל',
          autoClose: 3000,
        });
      }
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'שגיאה בשליחת הבקשה',
        message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsSubmittingApproval(false);
      submitInFlightRef.current = false;
    }
  }, [approvalRequestModalState, orderId, employeeSession, tenantId, isManager, onApprovalEvent, onApprovalSubmitted]);

  const logManagerAction = useCallback(async (payload: ApprovalPayload) => {
    if (!orderId || !employeeSession) return;
    await approvalsService.create(tenantId, orderId, {
      actionType: payload.actionType,
      reason: 'manager_action',
      note: null,
      payload,
    }, employeeSession);
  }, [tenantId, orderId, employeeSession]);

  const decide = useCallback(async (requestId: number, decision: 'approved' | 'denied') => {
    if (!employeeSession || decideInFlightRef.current) return;
    decideInFlightRef.current = true;
    setIsDeciding(true);
    try {
      const decided = await approvalsService.decide(tenantId, requestId, decision, employeeSession);
      onApprovalEvent(decided);
      const isLastPending = allPending.filter((r) => r.status === 'pending' && r.id !== requestId).length === 0;
      if (isLastPending) {
        setApprovalsModalOpened(false);
      }
    } catch (err) {
      notifications.show({
        color: 'red',
        title: 'שגיאה בעדכון הבקשה',
        message: toHebrewApiErrorMessage(err instanceof Error ? err.message : ''),
        autoClose: 3000,
      });
    } finally {
      setIsDeciding(false);
      decideInFlightRef.current = false;
    }
  }, [employeeSession, tenantId, onApprovalEvent, allPending]);

  const orderPendingRequests = orderId === null
    ? []
    : allPending.filter((r) => r.orderId === orderId);
  const pendingRequests = isManager ? allPending : orderPendingRequests;
  const historyRequests = sessionHistory.filter((request) =>
    approvalHistoryFilter === 'all' || request.status === approvalHistoryFilter,
  );

  return {
    allPendingCount: allPending.length,
    pendingRequests,
    historyRequests,
    approvalHistoryFilter,
    setApprovalHistoryFilter,
    isLoadingHistory,
    hasMoreHistory,
    loadMoreHistory: () => { void loadHistoryPage(historyCursor); },
    orderPendingRequests,
    hasPendingRequest: orderPendingRequests.length > 0,
    approvalsModalOpened,
    openApprovalsModal: () => setApprovalsModalOpened(true),
    closeApprovalsModal: () => setApprovalsModalOpened(false),
    approvalRequestModalState,
    isSubmittingApproval,
    isDeciding,
    requestApproval,
    closeApprovalRequestModal,
    submitApproval,
    onApprovalEvent,
    decide,
    logManagerAction,
  };
}
